/**
 * PortalAccountCard — the tech's way out of the old dead end (#329).
 *
 * The site's jurisdiction files through an online portal but has no account
 * number on file. Until now that 409'd "On My Way" and told the tech to open
 * a "Water & Reporting card" that exists only in the OFFICE app — no path
 * forward, on ~3,769 sites.
 *
 * Now: the job starts regardless, and this card is the tech's optional
 * contribution. Corbin, 2026-08-16 — "if the tech has it, I don't mind them
 * entering it, but if they enter it, it needs to be a notification to the
 * office and an approve/change situation."
 *
 * So the copy has to be honest about two things at once:
 *   1. this is NOT blocking you — go work the job;
 *   2. what you type does NOT go live; the office checks it first.
 * Anything vaguer and a tech either thinks they're stuck, or thinks the
 * number is filed when it isn't.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CheckCircle2, Clock, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface PendingProposal {
    id: string;
    proposedValue: string;
    createdAt: string;
}

export const PORTAL_ACCOUNT_MAX_LENGTH = 120;

export function PortalAccountCard({ jobId, platform }: { jobId: string; platform: string | null }) {
    const qc = useQueryClient();
    const [value, setValue] = useState("");

    const pendingQ = useQuery({
        queryKey: ["field", "water-account-proposal", jobId],
        queryFn: () =>
            api.get<{ proposal: PendingProposal | null }>(`/api/field/jobs/${jobId}/water-account-proposal`),
        staleTime: 30_000,
    });
    const pending = pendingQ.data?.proposal ?? null;

    const submit = useMutation({
        mutationFn: () =>
            api.post(`/api/field/jobs/${jobId}/water-account-proposal`, { accountNumber: value.trim() }),
        onSuccess: () => {
            toast.success("Sent to the office — they'll confirm it.");
            setValue("");
            void qc.invalidateQueries({ queryKey: ["field", "water-account-proposal", jobId] });
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Couldn't send the account number"),
    });

    const platformLabel = platform?.trim() || "the portal";

    // Already sent — don't offer an empty box the tech has filled once.
    if (pending) {
        return (
            <div
                className="mt-2 rounded-md border border-l-4 border-l-amber-500 bg-amber-50/60 p-2.5 dark:border-l-amber-400 dark:bg-amber-500/10"
                data-testid="portal-account-pending"
            >
                <div className="flex items-center gap-1.5 text-sm font-bold">
                    <Clock className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
                    Account # sent to the office
                </div>
                <p className="mt-1 text-xs text-foreground/80">
                    You sent <span className="font-mono font-semibold">{pending.proposedValue}</span> for{" "}
                    {platformLabel}. The office confirms it before it goes on file — nothing for you to do.
                </p>
            </div>
        );
    }

    return (
        <div
            className="mt-2 rounded-md border border-l-4 border-l-amber-500 bg-amber-50/60 p-2.5 dark:border-l-amber-400 dark:bg-amber-500/10"
            data-testid="portal-account-card"
        >
            <div className="flex items-center gap-1.5 text-sm font-bold">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                Go ahead and work this job
            </div>
            <p className="mt-1 text-xs text-foreground/80">
                The office doesn&apos;t have this address&apos;s {platformLabel} account # yet. That&apos;s their
                job to chase, not yours — <span className="font-semibold">it won&apos;t stop you</span>. If
                it&apos;s on the tag or you know it, send it over and save them the call.
            </p>
            <form
                className="mt-2 flex gap-2"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (value.trim()) submit.mutate();
                }}
            >
                <Input
                    data-testid="portal-account-proposal-input"
                    inputMode="text"
                    placeholder="Account # (optional)"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    maxLength={PORTAL_ACCOUNT_MAX_LENGTH}
                    className="h-11 flex-1 font-mono"
                />
                <Button
                    type="submit"
                    className="h-11 shrink-0"
                    disabled={!value.trim() || submit.isPending}
                    data-testid="portal-account-proposal-send"
                >
                    {submit.isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <>
                            <Send className="mr-1 h-4 w-4" />
                            Send
                        </>
                    )}
                </Button>
            </form>
            <p className="mt-1.5 text-[11px] text-foreground/70">
                Goes to the office to check first — it isn&apos;t filed with the city until they approve it.
            </p>
        </div>
    );
}
